import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { doc, setDoc, deleteDoc, updateDoc, collection, getDocs, query, where, serverTimestamp, Timestamp, increment } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getUserDisplayName } from '../utils/nameFormat';
import { REACTION_EMOJIS } from '../constants/reactions';
import './MatchReactionStrip.css';

/**
 * Aggregated reaction strip (LinkedIn/Facebook style): one row of emoji + count per team.
 * Clicking a count opens the full "reactions by team and emoji" modal (via onOpenReactionsModal).
 * @param {string} matchId - Match document id
 * @param {'player1'|'player2'} team - Team side
 * @param {string} teamName - Display name for the team/player
 * @param {Record<string, number>} reactionCounts - Map emojiIndex (string) -> count (from match.reactionCounts?.[team])
 * @param {function(matchId, team, emojiIndex, delta): void} onReactionUpdate - Callback for optimistic list update
 * @param {function(matchId): void} onOpenReactionsModal - Callback when user clicks a count (open full breakdown modal)
 */
export default function MatchReactionStrip({ matchId, team, teamName, reactionCounts = {}, onReactionUpdate, onOpenReactionsModal }) {
  const { currentUser, userProfile } = useAuth();
  const [myReactions, setMyReactions] = useState(new Set());
  const [loadingMy, setLoadingMy] = useState(true);

  useEffect(() => {
    if (!matchId || !currentUser?.uid) {
      setLoadingMy(false);
      return;
    }
    const reactionsRef = collection(db, 'matches', matchId, 'reactions');
    const q = query(reactionsRef, where('userId', '==', currentUser.uid), where('team', '==', team));
    getDocs(q)
      .then((snap) => {
        const mine = new Set();
        snap.docs.forEach((d) => {
          const emoji = d.data().emoji;
          if (REACTION_EMOJIS.includes(emoji)) mine.add(emoji);
        });
        setMyReactions(mine);
      })
      .catch(() => setMyReactions(new Set()))
      .finally(() => setLoadingMy(false));
  }, [matchId, team, currentUser?.uid]);

  async function toggleReaction(emoji) {
    if (!matchId || !currentUser?.uid) return;
    const emojiIndex = String(REACTION_EMOJIS.indexOf(emoji));
    if (emojiIndex === '-1') return;
    const docId = `${currentUser.uid}_${emojiIndex}_${team}`;
    const reactionRef = doc(db, 'matches', matchId, 'reactions', docId);
    const matchRef = doc(db, 'matches', matchId);
    if (myReactions.has(emoji)) {
      try {
        await deleteDoc(reactionRef);
        await updateDoc(matchRef, {
          [`reactionCounts.${team}.${emojiIndex}`]: increment(-1),
          updatedAt: Timestamp.now()
        });
        setMyReactions((prev) => {
          const next = new Set(prev);
          next.delete(emoji);
          return next;
        });
        onReactionUpdate?.(matchId, team, emojiIndex, -1);
      } catch (err) {
        console.warn('Remove reaction failed:', err);
      }
    } else {
      const displayName = getUserDisplayName({ displayName: userProfile?.displayName || currentUser.displayName, email: currentUser.email });
      try {
        await setDoc(reactionRef, {
          userId: currentUser.uid,
          emoji,
          emojiIndex,
          team,
          displayName: displayName || 'Someone',
          createdAt: serverTimestamp()
        });
        await updateDoc(matchRef, {
          [`reactionCounts.${team}.${emojiIndex}`]: increment(1),
          updatedAt: Timestamp.now()
        });
        setMyReactions((prev) => new Set(prev).add(emoji));
        onReactionUpdate?.(matchId, team, emojiIndex, 1);
      } catch (err) {
        console.warn('Add reaction failed:', err);
      }
    }
  }

  if (!currentUser) return null;

  const totalForTeam = REACTION_EMOJIS.reduce((sum, emoji) => sum + (Number(reactionCounts[String(REACTION_EMOJIS.indexOf(emoji))]) || 0), 0);

  return (
    <div className="reaction-strip-row reaction-strip-aggregated" aria-label={`Support ${teamName}`}>
      <span className="reaction-strip-label">{teamName}</span>
      <div className="reaction-strip-aggregated-counts">
        {REACTION_EMOJIS.map((emoji) => {
          const emojiIndex = String(REACTION_EMOJIS.indexOf(emoji));
          const count = Number(reactionCounts[emojiIndex]) || 0;
          const isActive = !loadingMy && myReactions.has(emoji);
          return (
            <span key={emoji} className="reaction-strip-aggregated-item">
              <button
                type="button"
                onClick={() => toggleReaction(emoji)}
                className={`reaction-strip-btn reaction-strip-btn-sm ${isActive ? 'reaction-strip-btn-active' : ''}`}
                title={isActive ? 'Remove support' : `Support ${teamName}`}
                aria-pressed={isActive}
                disabled={loadingMy}
              >
                {emoji}
              </button>
              <button
                type="button"
                onClick={() => onOpenReactionsModal?.(matchId)}
                className="reaction-strip-count-sm"
                title="See all reactions by team and emoji"
              >
                {count}
              </button>
            </span>
          );
        })}
      </div>
      {totalForTeam > 0 && (
        <button
          type="button"
          onClick={() => onOpenReactionsModal?.(matchId)}
          className="reaction-strip-see-all"
          title="See all reactions by team and emoji"
        >
          {totalForTeam} reaction{totalForTeam !== 1 ? 's' : ''}
        </button>
      )}
    </div>
  );
}
